/**
 * Zóny výhodnosti: mřížka buněk nad mapou, pro každou buňku se spočítá
 * vybraná metrika cesty buňka → cíl B napříč zapnutými režimy.
 *
 * Hromadný výpočet používá výhradně odhadový model (vzdušná čára ×
 * nepřímost; u MHD předpočtené očekávané časy z každé zastávky), takže
 * je rychlý i pro tisíce buněk a nezávislý na externích službách.
 */
import { haversineKm, type Bounds } from './geo';
import { estimateUncertainty } from './planner';
import {
  buildExpectedField,
  expectedPtMinutes,
  fieldNearbyStops,
  type ExpectedPtField,
} from './transit';
import type {
  LatLng,
  ModeProfile,
  TransitNetwork,
  ZoneGrid,
  ZoneMetric,
} from './types';

export const ZONE_METRIC_LABELS: Record<ZoneMetric, string> = {
  bestMode: 'Nejlepší režim (čas)',
  weighted: 'Vážená kombinace',
  time: 'Celkový čas',
  wait: 'Čekání',
  cost: 'Cena',
  emissions: 'Emise CO₂',
  effort: 'Fyzická náročnost',
  comfort: 'Komfort',
  transfers: 'Přestupy',
  uncertainty: 'Nejistota odhadu',
};

export const ZONE_METRIC_UNITS: Record<ZoneMetric, string> = {
  bestMode: '',
  weighted: 'skóre',
  time: 'min',
  wait: 'min',
  cost: 'Kč',
  emissions: 'g CO₂',
  effort: '/5',
  comfort: '/5',
  transfers: '×',
  uncertainty: '± min',
};

/** Krátké vysvětlení metriky pro legendu/nápovědu. */
export const ZONE_METRIC_HELP: Record<ZoneMetric, string> = {
  bestMode: 'Barva místa = režim, který je odsud do cíle nejrychlejší.',
  weighted: 'Barva místa = režim s nejlepším poměrem času, ceny, komfortu a emisí podle vašich vah.',
  time: 'Jak dlouho trvá cesta do cíle nejrychlejším režimem.',
  wait: 'Kolik se odsud čeká (na spoj, sdílené vozidlo…).',
  cost: 'Kolik cesta do cíle stojí nejrychlejším režimem.',
  emissions: 'Emise CO₂ cesty do cíle.',
  effort: 'Jak fyzicky náročná je cesta (1 = bez námahy, 5 = velká námaha).',
  comfort: 'Pohodlí cesty (vyšší = lepší).',
  transfers: 'Počet přestupů cestou do cíle.',
  uncertainty: 'Jak moc se může skutečný čas lišit od odhadu.',
};

interface CellModeMetrics {
  time: number;
  wait: number;
  cost: number;
  emissions: number;
  effort: number;
  comfort: number;
  transfers: number;
  uncertainty: number;
  /** Krytí 0–1: u limitu vzdálenosti režim plynule bledne (žádná tvrdá hrana). */
  edge: number;
}

/** Plynulé vyblednutí u limitu: 1 do 80 % limitu, pak hladce k 0 na limitu. */
function edgeFade(crowKm: number, maxKm: number | undefined): number {
  if (maxKm === undefined || maxKm <= 0) return 1;
  const t = crowKm / maxKm;
  if (t <= 0.8) return 1;
  if (t >= 1) return 0;
  const x = (1 - t) / 0.2; // 1 → 0 na posledních 20 % limitu
  return x * x * (3 - 2 * x); // smoothstep
}

/**
 * Poctivost tvrzení „tady je nejlepší X": když i nejlepší varianta trvá
 * absurdně dlouho (např. jediná dostupná je 3h chůze, protože data MHD
 * danou oblast nepokrývají), barva slábne — silná barva = silné tvrzení.
 * 1 do 60 min, plynule k 0,25 na 180+ min.
 */
function sanityFade(timeMin: number): number {
  if (timeMin <= 60) return 1;
  if (timeMin >= 180) return 0.25;
  const x = 1 - (timeMin - 60) / 120;
  return 0.25 + 0.75 * (x * x * (3 - 2 * x));
}

/** Výchozí fyzická náročnost podle druhu režimu (1–5). */
export function defaultEffort(kind: ModeProfile['kind']): number {
  switch (kind) {
    case 'walk':
      return 3.5;
    case 'bike':
      return 3;
    case 'skate':
      return 4.5;
    case 'micro':
      return 2;
    case 'car':
      return 1;
    case 'pt':
      return 1.5;
    case 'combined':
      return 2.5;
    default:
      return 3;
  }
}

export interface ZoneWeights {
  time: number;
  cost: number;
  comfort: number;
  emissions: number;
}

/**
 * Vážené skóre buňky pro metriku „weighted" (nižší = lepší).
 * Normalizace pevnými škálami (60 min, 100 Kč, 1000 g CO₂), aby šlo
 * skóre porovnávat mezi buňkami bez globálního průchodu navíc.
 */
function weightedScore(m: CellModeMetrics, w: ZoneWeights): number {
  const wSum = w.time + w.cost + w.comfort + w.emissions || 1;
  const tN = Math.min(2, m.time / 60);
  const cN = Math.min(2, m.cost / 100);
  const pN = (5 - m.comfort) / 4;
  const eN = Math.min(2, m.emissions / 1000);
  return (w.time * tN + w.cost * cN + w.comfort * pN + w.emissions * eN) / wSum;
}

/** Metriky režimu z bodu p do cíle — odhadový model (bez externích dat). */
function evalMode(
  p: LatLng,
  mode: ModeProfile,
  crowKm: number,
  ptField: ExpectedPtField | null,
  net: TransitNetwork,
  walk: { speedKmh: number; circuity: number },
  calib?: Record<string, number>,
): CellModeMetrics | null {
  if (mode.kind === 'pt' || mode.kind === 'combined') {
    if (!ptField) return null;
    const accessSpeed = mode.kind === 'combined' ? Math.max(mode.speedKmh, 12) : walk.speedKmh;
    const accessCirc = walk.circuity;
    const maxAccess =
      mode.kind === 'combined' ? Math.max(net.maxAccessKm, 5) : net.maxAccessKm;
    const r = expectedPtMinutes(p, ptField, accessSpeed, accessCirc, maxAccess);
    if (!Number.isFinite(r.minutes)) return null;
    const rideKm = crowKm * 1.2;
    const fare = net.lines.reduce((m, l) => Math.max(m, l.costFlat), 0);
    const avgEmis =
      net.lines.length > 0
        ? net.lines.reduce((s, l) => s + l.emissionsPerKm, 0) / net.lines.length
        : 40;
    const avgComfort =
      net.lines.length > 0
        ? net.lines.reduce((s, l) => s + l.comfort, 0) / net.lines.length
        : 3;
    // Přestupy: z nejbližší použitelné zastávky (přes prostorový index).
    let transfers = 0;
    let bestT = Infinity;
    for (const i of fieldNearbyStops(ptField, p, maxAccess)) {
      const km = haversineKm(p, ptField.stops[i]);
      if (km > maxAccess) continue;
      const t = (km * accessCirc * 60) / accessSpeed + ptField.minutesFromStop[i];
      if (t < bestT) {
        bestT = t;
        transfers = Math.max(0, ptField.boardings[i] - 1);
      }
    }
    const reliability = 0.85 * Math.pow(0.94, transfers);
    return {
      time: r.minutes,
      wait: r.wait,
      cost: fare,
      emissions: avgEmis * rideKm,
      effort: (mode.effort ?? defaultEffort(mode.kind)) + transfers * 0.3,
      comfort: avgComfort,
      transfers,
      uncertainty: estimateUncertainty(r.minutes, reliability, false),
      edge: edgeFade(r.accessKm, maxAccess),
    };
  }

  // Přímé režimy.
  if (mode.maxDistanceKm !== undefined && crowKm > mode.maxDistanceKm) return null;
  if (mode.minDistanceKm !== undefined && crowKm < mode.minDistanceKm) return null;
  if (mode.speedKmh <= 0) return null;
  // Kalibrace reálnou trasou: poměr skutečné a odhadnuté délky A→B.
  const km = crowKm * mode.circuity * (calib?.[mode.id] ?? 1);
  const time = mode.setupMin + mode.waitMin + (km / mode.speedKmh) * 60 + mode.teardownMin;
  return {
    time,
    wait: mode.waitMin,
    cost: mode.costFixed + mode.costPerKm * km,
    emissions: mode.emissionsPerKm * km,
    effort: mode.effort ?? defaultEffort(mode.kind),
    comfort: mode.comfort,
    transfers: 0,
    uncertainty: estimateUncertainty(time, mode.reliability, false),
    edge: edgeFade(crowKm, mode.maxDistanceKm),
  };
}

export interface ZoneParams {
  bounds: Bounds;
  destination: LatLng;
  modes: ModeProfile[];
  transit: TransitNetwork;
  metric: ZoneMetric;
  /** Počet sloupců mřížky (řádky dle poměru stran), výchozí 56. */
  cols?: number;
  /** Jen tyto režimy (id) vstupují do výpočtu; undefined = všechny zapnuté. */
  modeIds?: string[] | null;
  /** Čas odjezdu (ms) — čekání na spoje dle jízdního řádu v tomto čase. */
  departMs?: number;
  /** Váhy pro metriku „weighted". */
  weights?: ZoneWeights;
  /**
   * Lokální kalibrace nepřímosti z REÁLNÝCH tras (modeId → násobič).
   * Plánovač zná skutečnou délku trasy A→B; poměr k odhadu (vzdušná ×
   * nepřímost) zachycuje terén (řeky, rybníky, zástavba) a zóny ho
   * převezmou — odhad buněk pak sedí na to, co uživatel vidí ve výsledcích.
   */
  circuityCalibration?: Record<string, number>;
}

/**
 * Spočítá mřížku zón. Pro každou buňku vybere NEJVÝHODNĚJŠÍ režim podle
 * zvolené metriky (čas → nejrychlejší, cena → nejlevnější, komfort →
 * nejpohodlnější, weighted → nejlepší vážené skóre…) a vrátí hodnotu
 * této metriky (u bestMode/weighted se barví barvou vítězného režimu).
 *
 * Složitost: O(buňky × režimy) + jeden předpočet MHD pole (Bellman–Ford
 * nad zastávkami). Pro 56×N buněk jde o tisíce levných vyhodnocení — bez
 * síťových volání, vhodné i pro přepočet při posunu časového posuvníku.
 */
export function computeZoneGrid(params: ZoneParams): ZoneGrid {
  const { bounds, destination, transit, metric } = params;
  const wanted = params.modeIds;
  const modes = params.modes.filter(
    (m) => m.enabled && (!wanted || wanted.length === 0 || wanted.includes(m.id)),
  );
  const cols = params.cols ?? 56;
  const weights = params.weights ?? { time: 1, cost: 0.5, comfort: 0.3, emissions: 0.3 };
  const latSpan = bounds.north - bounds.south;
  const lngSpan = bounds.east - bounds.west;
  // Poměr stran v metrech, ať jsou buňky zhruba čtvercové.
  const midLat = (bounds.north + bounds.south) / 2;
  const aspect =
    (latSpan * 110.574) / Math.max(1e-9, lngSpan * 111.32 * Math.cos((midLat * Math.PI) / 180));
  const rows = Math.max(8, Math.min(90, Math.round(cols * aspect)));

  const walkMode = params.modes.find((m) => m.id === 'walk');
  const walk = walkMode
    ? { speedKmh: walkMode.speedKmh, circuity: walkMode.circuity }
    : { speedKmh: 4.8, circuity: 1.25 };

  const needsPt = modes.some((m) => m.kind === 'pt' || m.kind === 'combined');
  const ptField = needsPt
    ? buildExpectedField(destination, transit, walk.speedKmh, walk.circuity, params.departMs)
    : null;

  const cells: ZoneGrid['cells'] = [];
  let min = Infinity;
  let max = -Infinity;

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const lat = bounds.south + ((r + 0.5) / rows) * latSpan;
      const lng = bounds.west + ((c + 0.5) / cols) * lngSpan;
      const p = { lat, lng };
      const crowKm = haversineKm(p, destination);

      // Nejvýhodnější režim PODLE ZVOLENÉ METRIKY (ne vždy podle času).
      let bestModeId = '';
      let bestScore = Infinity;
      let bestMetrics: CellModeMetrics | null = null;
      for (const mode of modes) {
        const m = evalMode(p, mode, crowKm, ptField, transit, walk, params.circuityCalibration);
        if (!m) continue;
        const s = selectionScore(m, metric, weights);
        if (s < bestScore) {
          bestScore = s;
          bestModeId = mode.id;
          bestMetrics = m;
        }
      }

      let value = NaN;
      if (bestMetrics) {
        value =
          metric === 'bestMode'
            ? modes.findIndex((m) => m.id === bestModeId)
            : metric === 'weighted'
              ? weightedScore(bestMetrics, weights)
              : bestMetrics[metric];
        if (metric !== 'bestMode' && Number.isFinite(value)) {
          min = Math.min(min, value);
          max = Math.max(max, value);
        }
      }
      cells.push({
        lat,
        lng,
        value,
        bestModeId,
        // Krytí: blednutí u limitů vzdálenosti × poctivost u absurdních časů.
        alpha: bestMetrics ? bestMetrics.edge * sanityFade(bestMetrics.time) : 1,
      });
    }
  }

  if (!Number.isFinite(min)) {
    min = 0;
    max = 1;
  }

  return {
    cells,
    cols,
    rows,
    bounds,
    metric,
    min,
    max,
    legend: buildLegend(metric, min, max, modes),
  };
}

/**
 * Hodnota, podle které se vybírá vítězný režim buňky (nižší = lepší).
 * bestMode drží historické chování (čas); komfort se obrací.
 */
function selectionScore(m: CellModeMetrics, metric: ZoneMetric, w: ZoneWeights): number {
  switch (metric) {
    case 'bestMode':
    case 'time':
      return m.time;
    case 'weighted':
      return weightedScore(m, w);
    case 'comfort':
      return -m.comfort;
    case 'wait':
      return m.wait;
    case 'cost':
      return m.cost;
    case 'emissions':
      return m.emissions;
    case 'effort':
      return m.effort;
    case 'transfers':
      return m.transfers;
    case 'uncertainty':
      return m.uncertainty;
  }
}

/* ------------------------------------------------------------------ */
/* Barvy a legenda                                                     */
/* ------------------------------------------------------------------ */

/** Plynulý gradient (modrá → tyrkys → žlutá → oranžová) pro číselné metriky. */
const GRADIENT: Array<[number, [number, number, number]]> = [
  [0, [38, 100, 235]],
  [0.35, [22, 178, 172]],
  [0.65, [241, 196, 15]],
  [1, [235, 87, 35]],
];

export function gradientColor(t: number): [number, number, number] {
  const x = Math.max(0, Math.min(1, t));
  for (let i = 1; i < GRADIENT.length; i++) {
    const [t1, c1] = GRADIENT[i - 1];
    const [t2, c2] = GRADIENT[i];
    if (x <= t2) {
      const f = (x - t1) / (t2 - t1);
      return [
        Math.round(c1[0] + (c2[0] - c1[0]) * f),
        Math.round(c1[1] + (c2[1] - c1[1]) * f),
        Math.round(c1[2] + (c2[2] - c1[2]) * f),
      ];
    }
  }
  return GRADIENT[GRADIENT.length - 1][1];
}

export function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [120, 120, 120];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const rgbToHex = (c: [number, number, number]): string =>
  `#${c.map((x) => x.toString(16).padStart(2, '0')).join('')}`;

/** Barva buňky: u bestMode/weighted barva vítězného režimu, jinak gradient min→max. */
export function cellColor(
  grid: ZoneGrid,
  value: number,
  bestModeId: string,
  modes: ModeProfile[],
): [number, number, number] | null {
  if (grid.metric === 'bestMode' || grid.metric === 'weighted') {
    const mode = modes.find((m) => m.id === bestModeId);
    if (!mode || !bestModeId) return null;
    return hexToRgb(mode.color);
  }
  if (!Number.isFinite(value)) return null;
  const span = grid.max - grid.min;
  const t = span < 1e-9 ? 0 : (value - grid.min) / span;
  // U komfortu je vyšší lepší — otočíme škálu, ať „lepší" je vždy modré.
  return gradientColor(grid.metric === 'comfort' ? 1 - t : t);
}

function buildLegend(
  metric: ZoneMetric,
  min: number,
  max: number,
  modes: ModeProfile[],
): ZoneGrid['legend'] {
  if (metric === 'bestMode' || metric === 'weighted') {
    return modes.filter((m) => m.enabled).map((m) => ({ color: m.color, label: m.name }));
  }
  const unit = ZONE_METRIC_UNITS[metric];
  const stops = [0, 0.25, 0.5, 0.75, 1];
  return stops.map((t) => {
    const v = min + (max - min) * t;
    const shown = metric === 'comfort' ? 1 - t : t;
    return {
      color: rgbToHex(gradientColor(shown)),
      label: `${formatLegendValue(v, metric)} ${unit}`.trim(),
    };
  });
}

function formatLegendValue(v: number, metric: ZoneMetric): string {
  if (metric === 'cost' || metric === 'emissions') return String(Math.round(v));
  if (metric === 'transfers') return String(Math.round(v));
  if (metric === 'weighted') return (Math.round(v * 100) / 100).toString();
  return (Math.round(v * 10) / 10).toString();
}
